import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminMenuAPI, getPartnerMenuAPI } from '../api/auth.api';
import { addPermissionChangeListener, useUser } from '../store/userStore';

export const useUserMenu = () => {
  const user = useUser();
  const queryClient = useQueryClient();

  // 사용자 정보를 기반으로 적절한 API 함수 선택
  const getMenuAPI = () => {
    // 사용자 역할 확인 (roles 배열에서 확인)
    const userRoles = user?.roles?.map((role) => role.name) || [];

    if (
      userRoles.includes('partner_employee') ||
      userRoles.includes('partner_manager')
    ) {
      return getPartnerMenuAPI;
    }
    return getAdminMenuAPI;
  };

  const queryKey = (() => {
    const userRoles = user?.roles?.map((role) => role.name) || [];
    if (
      userRoles.includes('partner_employee') ||
      userRoles.includes('partner_manager')
    ) {
      return ['partner-menu'];
    }
    return ['admin-menu'];
  })();

  const {
    data: menuData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: getMenuAPI(),
    staleTime: 1000 * 60 * 5, // 5분 캐시
    retry: 1,
    refetchOnWindowFocus: false, // 윈도우 포커스 시 재요청 방지
    enabled: !!user && !!user.permissions && user.permissions.length > 0, // 사용자 정보와 권한이 모두 있을 때만 실행
  });

  // 메뉴 강제 새로고침 함수
  const forceRefreshMenu = useCallback(() => {
    console.log('[useUserMenu] 메뉴 강제 새로고침 시작');

    // 모든 관련 쿼리 무효화
    queryClient.invalidateQueries({ queryKey: ['admin-menu'] });
    queryClient.invalidateQueries({ queryKey: ['partner-menu'] });
    queryClient.invalidateQueries({ queryKey });

    // 현재 메뉴 다시 로드
    refetch();
  }, [queryClient, queryKey, refetch]);

  // 전역 권한 변경 이벤트 리스너 등록
  useEffect(() => {
    const removeListener = addPermissionChangeListener((updatedUser) => {
      console.log('[useUserMenu] 전역 권한 변경 이벤트 수신 - 메뉴 새로고침', {
        권한수: updatedUser?.permissions?.length || 0,
      });

      // 즉시 메뉴 새로고침
      forceRefreshMenu();
    });

    return removeListener;
  }, [forceRefreshMenu]);

  // 추가 보장: 권한 변경을 직접 감지하여 메뉴를 자동 새로고침
  const permissionHash =
    user?.permissions
      ?.map((p) => p.name)
      .sort()
      .join(',') || '';
  const permissionCount = user?.permissions?.length || 0;

  useEffect(() => {
    if (user && user.permissions && permissionCount > 0) {
      console.log('[useUserMenu] 직접 권한 변경 감지됨 - 메뉴 새로고침 중...', {
        permissionCount,
        hashPreview: permissionHash.substring(0, 50) + '...',
      });
      forceRefreshMenu();
    }
  }, [permissionHash]);

  // 메뉴별 필요 권한 매핑
  const getRequiredPermissions = (menuPath: string): string[] => {
    const pathPermissionMap: Record<string, string[]> = {
      '/admin/dashboard': ['admin:dashboard_read'],
      '/admin/product': ['product:read'],
      '/admin/brand': ['brand:read'],
      '/admin/coupon': ['coupon:read'],
      '/admin/mileage': ['mileage:read'],
      '/admin/settlement': ['settlement:read'],
      '/admin/delivery': ['delivery:read'],
      '/admin/app-push': ['app_push:read'],
      '/admin/magazine': ['magazine:read'],
      '/admin/magazine-management': ['magazine-management:read'],
      '/admin/spot-time': ['spot_time:read'],
      '/admin/pick-round': ['pick_round:read'],
      '/admin/idus': ['idus:read'],
      '/admin/partner': ['partner:read'],
      '/admin/referral': ['referral:read'],
      '/admin/home-banner': ['home_banner:read'],
      '/admin/partner-dashboard': [
        'partner:dashboard_read',
        'partner-admin:dashboard_read',
      ],
      '/admin/partner-settlement': ['partner-admin:settlement_read'],
      '/admin/deeplink': ['deeplink:read'],
    };

    return pathPermissionMap[menuPath] || [];
  };

  // 서버에서 이미 권한 기반으로 필터링된 메뉴를 받으므로, 추가 필터링 없이 그대로 사용
  const filteredMenuItems = menuData?.menuItems || [];

  // 디버깅을 위한 로그 (개발 환경에서만)
  if (process.env.NODE_ENV === 'development') {
    console.log('[useUserMenu] 서버에서 받은 메뉴 아이템들:', {
      총개수: filteredMenuItems.length,
      메뉴목록: filteredMenuItems.map((item: any) => ({
        id: item.id,
        title: item.title,
        path: item.path,
      })),
    });
  }

  return {
    menuItems: filteredMenuItems,
    userType: menuData?.userType || user?.organizationType,
    partnerName: menuData?.partnerName || user?.partnerName,
    isLoading,
    error,
    forceRefreshMenu,
  };
};
